// Score saisi prioritaire et fraîcheur des scores FFBB (D-113) : logique pure, horloge injectée.
//  - Le score d'un match porte sa source : saisi par le club (stats, coach, joueuses) ou la FFBB.
//  - Rien de saisi : le score FFBB est pris ; un 0-0 de la FFBB est pris avec une alerte au coach.
//  - Score saisi : il reste ; écart avec la FFBB : il reste, alerte au coach (garder le nôtre ou
//    prendre celui de la FFBB). Un « pas joué » FFBB n'efface jamais un score.

export type ScoreSide = 'us' | 'them';

export interface ScoreFields {
  score_us: number | null; score_them: number | null; forfeit: ScoreSide | null;
  score_source?: 'club' | 'ffbb' | null;
  ffbb_score_us?: number | null; ffbb_score_them?: number | null; ffbb_forfeit?: ScoreSide | null;
  score_alert?: 'gap' | 'zero' | null;
  result_confirmed?: boolean;
}

/** « 64 – 52 », « gagné par forfait », « » sans score. */
export function scoreText(us: number | null | undefined, them: number | null | undefined, ff: ScoreSide | null | undefined): string {
  if (ff) return ff === 'them' ? 'gagné par forfait' : 'perdu par forfait';
  return us !== null && us !== undefined && them !== null && them !== undefined ? `${us} – ${them}` : '';
}

export interface ScoreAlertView {
  kind: 'gap' | 'zero';
  eyebrow: string;
  /** La phrase du bandeau. */
  text: string;
  ffbb: string;
  ours: string;
}

/** Le bandeau du staff sur la page du match, ou null s'il n'y a rien à trancher. */
export function scoreAlertView(m: ScoreFields): ScoreAlertView | null {
  if (!m.score_alert) return null;
  const ffbb = scoreText(m.ffbb_score_us, m.ffbb_score_them, m.ffbb_forfeit);
  const ours = scoreText(m.score_us, m.score_them, m.forfeit);
  if (m.score_alert === 'zero') {
    return { kind: 'zero', eyebrow: 'Score FFBB à vérifier', text: 'Score FFBB 0-0, sûrement une erreur', ffbb: ffbb || '0 – 0', ours };
  }
  return { kind: 'gap', eyebrow: 'Score FFBB différent', text: `La FFBB affiche ${ffbb || 'un autre score'} ; le score saisi au club reste.`, ffbb, ours };
}

/** Le résultat compte (annonces, D-110) : forfait, ou score connu, sauf un 0-0 venu de la FFBB. Repli
 *  pour une ligne sans `result_confirmed` (calculé par la base). */
export function resultConfirmed(m: ScoreFields): boolean {
  if (typeof m.result_confirmed === 'boolean') return m.result_confirmed;
  if (m.forfeit) return true;
  if (m.score_us === null || m.score_them === null) return false;
  return !(m.score_source === 'ffbb' && m.score_us === 0 && m.score_them === 0);
}

/** « Récupérer le score » : un match relié à la FFBB, commencé, dont le résultat n'est pas confirmé. */
export function canFetchScore(m: ScoreFields & { ffbb_match_id?: string | null; live?: boolean }, start: Date, now: Date = new Date()): boolean {
  return !!m.ffbb_match_id && !m.live && now.getTime() >= start.getTime() && !resultConfirmed(m);
}

/** Ce que la base répond à « Récupérer le score » (ffbb_score_claim). */
export type ScoreClaim =
  | { state: 'read'; poule_id: string; staff_links: { team_id: string; engagement_id: string; poule_id: string; competition_name: string }[] }
  | { state: 'recent'; read_at: string; published: boolean }
  | { state: 'busy' | 'confirmed' | 'early' | 'none' };

/** Le match après une lecture déposée (ffbb_poule_apply, p_event). */
export interface FetchedMatch { score_us: number | null; score_them: number | null; forfeit: ScoreSide | null; result_confirmed: boolean; published: boolean; score_alert: 'gap' | 'zero' | null }

/** Le retour clair de « Récupérer le score » : « Score à jour », « Pas encore publié par la FFBB », « Lu il y a 5 min ». */
export function fetchVerdict(claim: ScoreClaim, after: FetchedMatch | null = null, now: Date = new Date()): { text: string; done: boolean } {
  switch (claim.state) {
    case 'confirmed': return { text: 'Score à jour', done: true };
    case 'early': return { text: 'Le match n’a pas encore commencé', done: false };
    case 'none': return { text: 'Ce match n’est pas suivi sur la FFBB', done: false };
    case 'busy': return { text: 'Lecture en cours sur un autre téléphone : réessaie dans une minute', done: false };
    case 'recent': return { text: `Lu ${agoText(claim.read_at, now)}${claim.published ? '' : ' : pas encore publié par la FFBB'}`, done: false };
    case 'read':
      if (!after) return { text: 'FFBB injoignable pour l’instant', done: false };
      if (after.score_alert === 'zero') return { text: 'La FFBB affiche 0 – 0, sûrement une erreur : le staff est prévenu', done: false };
      if (after.result_confirmed) return { text: 'Score à jour', done: true };
      return { text: 'Pas encore publié par la FFBB', done: false };
  }
}

/** Les lectures en arrière-plan attendent : jamais pendant une saisie (stats, lien de saisie, champ actif). */
export function readsMayRun(hash: string, activeTag: string | null | undefined, liveScreen: boolean): boolean {
  if (liveScreen || /^#\/(stats|saisie|s)\//.test(hash)) return false;
  return !['INPUT', 'TEXTAREA', 'SELECT'].includes((activeTag ?? '').toUpperCase());
}

/** « à l'instant », « il y a 5 min », « il y a 2 h ». */
export function agoText(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const min = Math.max(0, Math.round((now.getTime() - Date.parse(iso)) / 60000));
  if (min < 1) return 'à l’instant';
  if (min < 60) return `il y a ${min} min`;
  return `il y a ${Math.round(min / 60)} h`;
}
